export interface Source {
  id: number;
  enabled: boolean;
  name: string;
  option_id: string;
  billable: boolean;
  created_by: number;
  updated_by: number;
  created_at: number;
  updated_at: number;
  destination_ids: number[];
  sync_count?: number;
  has_cio_sources: boolean;
  is_journeys_metrics_source: boolean;
  is_journeys_auto_track_source: boolean;
  // oxlint-disable-next-line typescript/no-explicit-any -- pre-existing explicit any; replace with a precise type when touched
  settings?: Record<string, any>;
  oauth_connected?: boolean;
  geolocation_enabled: boolean;
}

export interface SourceSyncTemplate {
  sync_type: string;
  title: string;
  subtitle: string;
  destination_icon: string;
  record_type: string;
  cdp_type: string;
  fields: string[];
  email_field?: string;
  // Relationship templates carry both identifiers; the client cannot derive
  // them (e.g. AccountContactRelation's Contact.Email / AccountId).
  user_field?: string;
  group_field?: string;
}

export interface IntegrationMetadataLink {
  name: string;
  url: string;
}

export interface IntegrationMetadataItem {
  label: string;
  value?: string;
  links?: IntegrationMetadataLink[];
}

export interface AvailableSource {
  option_id: string;
  icon: string;
  name: string;
  content: string;
  learn_more_url: string;
  category: string;
  code?: string;
  type: string;
  language?: string;
  library_install_command?: string;
  library_install_instructions?: string;
  library_install_learn_more_url?: string;
  hidden: boolean;
  premium?: boolean;
  beta: boolean;
  connection_type: string;
  feature_flag: string;
  geolocation_supported: boolean;
  integration_description?: string;
  features?: string;
  requirements?: string;
  integration_metadata?: IntegrationMetadataItem[];
  sync_templates?: SourceSyncTemplate[];
}

export interface SourceSync {
  id: number;
  source_id: number;
  type: string;
  name: string;
  description: string;
  enabled: boolean;
  mapping: ConnectorSourceRecordMapping;
  sync_interval: number;
  sync_timeout: number;
  sync_attempts: number;
  start_at?: number;
  last_run_at?: number;
  next_run_at?: number;
  enabled_at?: number;
  enabled_by: number;
  created_at: number;
  created_by: number;
  updated_at: number;
  updated_by: number;
}

export interface SourceSyncRun {
  id: number;
  source_sync_id: number;
  status: string;
  progress: number;
  processed_rows: number;
  total_rows: number;
  suppressed_rows: number;
  failed_rows: number;
  error?: string;
  started_at?: number;
  ended_at?: number;
  created_at: string | number;
  updated_at: string | number;
}

export interface ApiKey {
  id: number;
  api_key: string;
  created_by: number;
  source_id: number;
  created_at: number;
}

export interface SourceMetrics {
  timeseries: [number, number][];
  total: number;
}

export interface Traits {
  [key: string]: { lastSeenAt: number };
}

export interface SchemaEvent {
  name: string;
  properties: Traits;
}

export interface Schema {
  events: SchemaEvent[];
  traits: Traits;
}

export interface SourceEvent {
  type: string;
  messageId: string;
  userId: string;
  traits: Traits;
  timestamp: string;
  receivedAt: string;
  context?: {
    sync?: {
      id?: number;
      importId?: number;
      name?: string;
      recordType?: string;
    };
  };
}

export const SourceType = {
  CONNECTOR: 'connector',
  DATABASE: 'database',
} as const;
type SourceType = (typeof SourceType)[keyof typeof SourceType];

export interface CreateOrUpdateSourceSyncCmd {
  name: string;
  sync_interval: number;
  enabled: boolean;
  mapping: ConnectorSourceRecordMapping;
  config?: ConnectorSourceStreamConfig;
  start_at?: number | null;
}

export interface ConnectorSourceStreamConfig {
  supported_sync_modes: string[];
  source_defined_cursor: boolean;
  default_cursor_field: string[];
  source_defined_primary_key: string[][];
}

export interface ConnectorSourceRecord {
  name: string;
  label: string;
  labelPlural: string;
  fields: ConnectorSourceRecordField[];
  associateEntityType?: string;
  associateParentEntity?: string;
  config?: ConnectorSourceStreamConfig;
  recordTypes?: Array<{ name: string; id: string }>;
}

export interface ConnectorSourceRecordField {
  name: string;
  label: string;
  type: string;
  referenceTo?: string[];
}

export const ConnectorSourceTransformationType = {
  IDENTIFY: 'identify',
  GROUP: 'group',
  TRACK: 'track',
  RELATIONSHIP: 'relationship',
} as const;
export type ConnectorSourceTransformationType =
  (typeof ConnectorSourceTransformationType)[keyof typeof ConnectorSourceTransformationType];

export interface ConnectorSourceRecordMapping {
  record_type?: string;
  cdp_type?: ConnectorSourceTransformationType;
  identifiers?: {
    user_id?: string;
    group_id?: string;
    event?: string;
    object_name?: string;
  };
  fields?: string[];
  renames?: Record<string, string>;
  where_clause?: string;
  filters?: Record<string, Array<{ field: string; operator: string; value: string }>>;
  // Relationship syncs only: the People/Account sync ids linked at Enable
  // time by the paved-road drawer. Guards deleting/disabling those parents
  // while this sync is enabled. Frontend-owned — the API stores mapping as
  // raw JSON and the sync runner ignores unknown keys.
  linked_sync_ids?: number[];
}

export interface ConnectorSourceJourneysObjectMapping {
  objects: {
    /** Nullable: a mapping saved before the object was named carries no name. */
    name?: string | null;
    object_type_id: string;
  }[];
}

export interface MessageMetricsSourceSettings {
  events: string[];
  send_duplicates: boolean;
}
